/**
 * Standard error type for all service operations
 */
export class ServiceError extends Error {
    constructor({
        message,
        code = 'SERVICE_ERROR',
        statusCode,
        details,
        originalError = null,
        serviceName,
        endpoint,
        clientPayload = null
    }) {
        super(message);
        this.name = 'ServiceError';
        this.code = code;
        this.statusCode = statusCode;
        this.details = details;
        this.originalError = originalError;
        this.serviceName = serviceName;
        this.endpoint = endpoint;
        this.clientPayload = clientPayload;
        this.timestamp = new Date().toISOString();
    }

    /**
     * Creates a ServiceError from an API error response
     */
    static fromApiError(error, serviceName) {
        if (error instanceof ServiceError) {
            return error;
        }

        const baseError = {
            message: 'An operation error occurred',
            serviceName,
            originalError: error
        };

        if (error.meta?.error) {
            return new ServiceError({
                ...baseError,
                message: error.meta.error.message || baseError.message,
                code: error.meta.error.code || 'API_ERROR',
                statusCode: error.status,
                details: error.meta.error.details,
                endpoint: error.url,
                clientPayload: error.clientPayload
            });
        }

        // Handle API response errors
        if (error.response?.data) {
            return new ServiceError({
                ...baseError,
                message: error.response.data.error || error.response.data.message || baseError.message,
                code: error.response.data.code || 'API_ERROR',
                statusCode: error.response.status,
                details: error.response.data.meta?.error?.details || error.response.data.details,
                endpoint: error.response.data.url,
                clientPayload: error.response.data.clientPayload
            });
        }

        // Handle network or other errors
        if (error.error) {
            return new ServiceError({
                ...baseError,
                message: error.error,
                code: error.code || 'NETWORK_ERROR',
                details: error.details
            });
        }

        // Handle generic errors
        return new ServiceError({
            ...baseError,
            message: error.message || baseError.message,
            code: 'UNKNOWN_ERROR'
        });
    }

    /**
     * Creates a ServiceError for validation failures
     */
    static validation(message, details, serviceName) {
        return new ServiceError({
            message,
            code: 'VALIDATION_ERROR',
            details,
            serviceName
        });
    }

    /**
     * Creates a ServiceError for not found scenarios
     */
    static notFound(message, details, serviceName) {
        return new ServiceError({
            message,
            code: 'NOT_FOUND',
            statusCode: 404,
            details,
            serviceName
        });
    }

    /**
     * Creates a ServiceError for cache operations
     */
    static cache(message, details, serviceName) {
        return new ServiceError({
            message,
            code: 'CACHE_ERROR',
            details,
            serviceName
        });
    }
}
